import React, { useState, useMemo } from "react";
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import MeetingInfoForm from "./forms/MeetingInfoForm";
import MeetingScheduleForm from "./forms/MeetingScheduleForm";
import ConfirmationModal from "./ConfirmationModal";
import { useSendEmail } from "../../../api/sendEmailService";

const progressBarAnimation = {
    initial: { scaleX: 0 },
    animate: { scaleX: 1 },
    transition: (i) => ({ duration: 0.5, delay: i * 0.2 }),
};

export default function MeetingDrawer({ open, onClose }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        date: null,
        time: "",
        topic: "",
    });

    const { mutate: sendEmail, isLoading, isError, error } = useSendEmail();

    const handleChange = useMemo(() => (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const handleSubmit = useMemo(() => (event) => {
        event.preventDefault();
        const data = {
            userInfo: formData,
            type: "meeting",
        };
        sendEmail(data, {
            onSuccess: () => {
                setShowConfirmation(true);
                setFormData({
                    name: "",
                    email: "",
                    company: "",
                    phone: "",
                    date: null,
                    time: "",
                    topic: "",
                });
                setActiveStep(0);
                onClose();
                setTimeout(() => {
                    setShowConfirmation(false);
                }, 5000);
            },
            onError: (err) => {
                console.error("Submission error:", err.message);
            },
        });
    }, [formData, onClose, sendEmail]);

    const handleCloseConfirmation = useMemo(() => () => {
        setShowConfirmation(false);
    }, []);

    const handleNext = useMemo(() => () => {
        setActiveStep(prev => prev + 1);
    }, []);

    const handleBack = useMemo(() => () => {
        setActiveStep(prev => prev - 1);
    }, []);

    const headerText = useMemo(() =>
            activeStep === 0 ? t("drawer.meeting.detailsTitle") : t("drawer.meeting.scheduleTitle"),
        [activeStep, t]);

    const progressIndicators = useMemo(() =>
            [0, 1].map((step) => (
                <Box
                    key={step}
                    component={motion.div}
                    sx={{
                        height: "4px",
                        background: step <= activeStep ? theme.palette.primary.main : theme.palette.grey[300],
                        borderRadius: "4px",
                        flex: 1,
                        boxShadow: step <= activeStep ? `0 0 8px ${theme.palette.primary.main}50` : "none",
                    }}
                    initial={progressBarAnimation.initial}
                    animate={progressBarAnimation.animate}
                    transition={() => progressBarAnimation.transition(step)}
                />
            )),
        [activeStep, theme]);

    const formattedDateTime = useMemo(() => {
        const formattedDate = formData.date ? new Date(formData.date).toLocaleDateString() : "";
        return formattedDate && formData.time ? `${formattedDate} at ${formData.time}` : "";
    }, [formData.date, formData.time]);

    return (
        <>
            <Drawer
                anchor="left"
                open={open}
                onClose={onClose}
                sx={{ 
                    "& .MuiDrawer-paper": {
                        width: { xs: "100%", sm: "500px" },
                        maxWidth: "100%",
                        background: theme.palette.background.paper,
                        borderRight: `1px solid ${theme.palette.grey[200]}`,
                        boxShadow: `0 0 60px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.15)'}`,
                    }
                }}
            >
                <Box sx={{
                    px: { xs: 3, sm: 4 },
                    py: 4,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 4,
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                            }}
                        >
                            {headerText}
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: theme.palette.text.secondary,
                                "&:hover": {
                                    color: theme.palette.text.primary,
                                    backgroundColor: theme.palette.grey[100],
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ mb: 4 }}>
                        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                            {progressIndicators}
                        </Stack>
                    </Box>
                    {isLoading && (
                        <Typography sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                            {t("drawer.meeting.submitting", "Submitting...")}
                        </Typography>
                    )}
                    {isError && (
                        <Typography sx={{ color: theme.palette.error.main, mb: 2 }}>
                            {t("drawer.meeting.error", "Error:")} {error.message}
                        </Typography>
                    )}
                    {activeStep === 0 && (
                        <MeetingInfoForm
                            formData={formData}
                            handleChange={handleChange}
                            activeStep={activeStep}
                            handleNext={handleNext}
                        />
                    )}
                    {activeStep === 1 && (
                        <MeetingScheduleForm
                            formData={formData}
                            setFormData={setFormData}
                            activeStep={activeStep}
                            handleBack={handleBack}
                            handleSubmit={handleSubmit}
                            isSubmitting={isLoading}
                        />
                    )}
                </Box>
            </Drawer>
            <AnimatePresence>
                {showConfirmation && (
                    <ConfirmationModal
                        open={showConfirmation}
                        onClose={handleCloseConfirmation}
                        title={t("drawer.meeting.confirmationTitle", "Meeting Scheduled!")}
                        confimation={t("drawer.meeting.confirmation")}
                        message={t("drawer.meeting.confirmationMessage", "Thank you for scheduling your meeting with us.")}
                        details={formattedDateTime}
                        showDetails={!!formattedDateTime}
                    />
                )}
            </AnimatePresence>
        </>
    );
}